import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  Alert,
} from '@mui/material';
import { renderApprovalChips, getStatusColor, getCharterScheduleSpanHours, isLongerThanHours, formatDurationHours, LONG_CHARTER_HOURS, formatDateTime as formatSectorDate } from './SalesEnquiryUtils';

const TripOverview = ({ selectedTrip, sectors = [] }) => {
  if (!selectedTrip) {
    return null;
  }

  const charterHours = getCharterScheduleSpanHours(sectors);
  const firstSector = [...sectors]
    .filter((s) => s?.departure?.date)
    .sort((a, b) => (Number(a.seqNo) || 0) - (Number(b.seqNo) || 0))[0];
  const flightDateLabel = firstSector
    ? formatSectorDate(firstSector.departure.date)
    : 'N/A';

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ mb: 2 }}>
      {isLongerThanHours(charterHours) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This charter spans {formatDurationHours(charterHours)} from first departure to last
          arrival (over {LONG_CHARTER_HOURS} hours). Warning only — not a blocker.
        </Alert>
      )}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Charter Information
      </Typography>
      
      <Grid container spacing={2}>
        {/* Charter Details Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Charter Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Charter Number
                  </Typography>
                  <Typography variant="h6" fontWeight="medium">
                    {selectedTrip.charterNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Title
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.title}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Flight Date
                  </Typography>
                  <Typography variant="body1">
                    {flightDateLabel}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Flight Type
                  </Typography>
                  <Chip 
                    label={selectedTrip.flightType || 'CHARTER'} 
                    size="small" 
                    variant="outlined" 
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip 
                    label={selectedTrip.status} 
                    color={getStatusColor(selectedTrip.status)} 
                    size="small" 
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Flight Information Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Flight Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Passengers
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.passengers?.length || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Compliance Requirements
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      label="BA Required" 
                      size="small" 
                      color="warning"
                      title="Mandatory DGCA requirement"
                    />
                    <Chip 
                      label="ATC Required" 
                      size="small" 
                      color="info"
                      title="Mandatory DGCA requirement"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Client Information Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Client Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Client Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.clientName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.clientContact?.email || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.clientContact?.phone || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Metadata Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Metadata
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created By
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.createdBy 
                      ? `${selectedTrip.createdBy.firstName || ''} ${selectedTrip.createdBy.lastName || ''}`.trim() || selectedTrip.createdBy.email || 'Unknown'
                      : 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.createdAt ? formatDateTime(selectedTrip.createdAt) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.updatedAt ? formatDateTime(selectedTrip.updatedAt) : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Requirements & Approvals Card */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Requirements & Approvals
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Special Requirements
                  </Typography>
                  <Typography variant="body1">
                    {selectedTrip.specialRequirements || 'None'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Approval Status
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {renderApprovalChips(selectedTrip.pendingApprovals, selectedTrip.approvalStatus, 'trip')}
                    {!selectedTrip.pendingApprovals?.length && !selectedTrip.approvalStatus?.hasPendingApprovals && (
                      <Typography variant="caption" color="text.secondary">No approvals required.</Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TripOverview;
